import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AppReview } from '@capawesome/capacitor-app-review';
import * as Sentry from '@sentry/angular-ivy';
import { StorageService } from './storage.service';
import { UserService } from './user.service';
import { AppService } from './app.service';

const STORAGE_KEYS = {
  openCount: 'appOpenCount',
  firstOpenAt: 'firstAppOpenAt',
  lastReviewRequestAt: 'lastAppReviewRequest',
  lastPausedAt: 'appLastPausedAt',
} as const;

/** Minimum native sessions before an automatic review prompt. */
const MIN_OPENS_FOR_AUTO_REVIEW = 10;
/** App must have been installed/used at least this many days. */
const MIN_DAYS_SINCE_FIRST_OPEN = 7;
/** Space automatic (and menu) review prompts. */
const REVIEW_COOLDOWN_DAYS = 90;
/** Resume after this long in background counts as a new open. */
const MIN_BACKGROUND_MS_FOR_NEW_OPEN = 30 * 60 * 1000;
/** Delay so the prompt is not the first thing on screen. */
const AUTO_PROMPT_DELAY_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class AppReviewService {
  private promptScheduled = false;

  constructor(
    private storageService: StorageService,
    private userService: UserService,
    private appService: AppService,
  ) {}

  /** Call when the app goes to background so resume can detect a new session. */
  async markPaused(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    await this.storageService.set(STORAGE_KEYS.lastPausedAt, new Date().toISOString());
  }

  /**
   * Count a cold start, or a resume after a meaningful background period.
   * May schedule an automatic review prompt when thresholds are met.
   */
  async recordAppOpen(options: { force?: boolean } = {}): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      if (!options.force) {
        const lastPausedAt = await this.storageService.get(STORAGE_KEYS.lastPausedAt);
        if (lastPausedAt) {
          const pausedMs = Date.now() - new Date(lastPausedAt).getTime();
          if (pausedMs < MIN_BACKGROUND_MS_FOR_NEW_OPEN) {
            return;
          }
        }
      }

      const firstOpenAt = await this.storageService.get(STORAGE_KEYS.firstOpenAt);
      if (!firstOpenAt) {
        await this.storageService.set(STORAGE_KEYS.firstOpenAt, new Date().toISOString());
      }

      const currentCount = Number(await this.storageService.get(STORAGE_KEYS.openCount)) || 0;
      const openCount = currentCount + 1;
      await this.storageService.set(STORAGE_KEYS.openCount, openCount);

      await this.maybeRequestReviewAutomatically(openCount);
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: 'app-review', step: 'record-open' },
      });
    }
  }

  /** Explicit "Rate App" from the profile menu — cooldown only. */
  async requestReviewFromMenu(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.appService.showToast('App reviews are only available on mobile devices.');
      return;
    }

    try {
      const cooldown = await this.getCooldownDaysRemaining();
      if (cooldown > 0) {
        this.appService.showToast(
          `Thank you! You can review again in ${cooldown} days.`
        );
        return;
      }

      await AppReview.requestReview();
      await this.storageService.set(
        STORAGE_KEYS.lastReviewRequestAt,
        new Date().toISOString()
      );
      this.appService.showToastWithDuration(
        'Thank you for your feedback!',
        2000,
        'success'
      );
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: {
          feature: 'app-review',
          source: 'menu-page',
          user_id: this.userService.getAppUser()?.uid,
        },
      });
      this.appService.showToast('Unable to request app review.');
    }
  }

  /**
   * Re-check eligibility after login. Opens may have been counted before
   * auth finished, so the automatic prompt would have been skipped.
   */
  async tryRequestReviewAfterAuth(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const openCount = Number(await this.storageService.get(STORAGE_KEYS.openCount)) || 0;
    await this.maybeRequestReviewAutomatically(openCount);
  }

  private async maybeRequestReviewAutomatically(openCount: number): Promise<void> {
    if (this.promptScheduled) {
      return;
    }

    const user = this.userService.getAppUser();
    if (!user) {
      return;
    }

    if (openCount < MIN_OPENS_FOR_AUTO_REVIEW) {
      return;
    }

    const firstOpenAt = await this.storageService.get(STORAGE_KEYS.firstOpenAt);
    if (!firstOpenAt) {
      return;
    }

    const daysSinceFirstOpen = this.daysBetween(new Date(firstOpenAt), new Date());
    if (daysSinceFirstOpen < MIN_DAYS_SINCE_FIRST_OPEN) {
      return;
    }

    const cooldown = await this.getCooldownDaysRemaining();
    if (cooldown > 0) {
      return;
    }

    this.promptScheduled = true;
    setTimeout(async () => {
      try {
        // Re-check cooldown in case menu prompt ran during the delay.
        const stillCoolingDown = await this.getCooldownDaysRemaining();
        if (stillCoolingDown > 0) {
          return;
        }

        await AppReview.requestReview();
        await this.storageService.set(
          STORAGE_KEYS.lastReviewRequestAt,
          new Date().toISOString()
        );
      } catch (error) {
        Sentry.captureException(error?.originalError || error, {
          tags: {
            feature: 'app-review',
            source: 'auto-open-count',
            user_id: user.uid,
          },
          extra: {
            openCount,
            daysSinceFirstOpen,
            platform: Capacitor.getPlatform(),
          },
        });
      } finally {
        this.promptScheduled = false;
      }
    }, AUTO_PROMPT_DELAY_MS);
  }

  private async getCooldownDaysRemaining(): Promise<number> {
    const lastReviewDate = await this.storageService.get(
      STORAGE_KEYS.lastReviewRequestAt
    );
    if (!lastReviewDate) {
      return 0;
    }

    const daysSince = this.daysBetween(new Date(lastReviewDate), new Date());
    if (daysSince >= REVIEW_COOLDOWN_DAYS) {
      return 0;
    }
    return REVIEW_COOLDOWN_DAYS - daysSince;
  }

  private daysBetween(from: Date, to: Date): number {
    const diffMs = Math.abs(to.getTime() - from.getTime());
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }
}
